import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { LoginForm } from '../components/LoginForm';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import Logo from '../../../shared/components/ui/Logo';
import { supabase } from '../../../shared/lib/supabase';

const LoginPage = () => {
  
  const navigate = useNavigate();
  const { user, loading, signIn } = useAuth();
  const [currentView, setCurrentView] = useState<'login' | 'forgot-password' | 'reset-sent'>('login');
  const [resetAttempts, setResetAttempts] = useState<Record<string, { count: number; lastAttempt: number }>>({});
  const [currentEmail, setCurrentEmail] = useState<string>('');

  // Debug logging
  console.log('🔍 LoginPage render - user:', user?.email, 'loading:', loading);

  // Redirect to organization selection if already authenticated
  useEffect(() => {
    console.log('🔍 LoginPage useEffect - user:', user?.email, 'loading:', loading);
    if (user && !loading) {
      console.log('🚀 Redirecting to organization selection...');
      navigate('/select-organization');
    }
  }, [user, loading, navigate]);

  const handleLogin = async (email: string, password: string) => {
    try {
      console.log('🔐 Attempting login for:', email);
      await signIn(email, password);
      console.log('✅ Login successful, redirecting...');
      // The redirect will happen automatically via the useEffect above
    } catch (error) {
      console.error('❌ Login error:', error);
      // Error handling is done in the LoginForm component
    }
  };

  const handleForgotPassword = () => {
    console.log('🔑 Forgot password clicked');
    setCurrentView('forgot-password');
  };

  const handleBackToLogin = () => {
    setCurrentView('login');
  };

  const handleResetPassword = async (email: string) => {
    try {
      console.log('🔑 Resetting password for:', email);
      
      // Rate limiting: Check if user has exceeded attempts (max 3 per hour)
      const now = Date.now();
      const userAttempts = resetAttempts[email];
      
      if (userAttempts) {
        const timeDiff = now - userAttempts.lastAttempt;
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
        
        if (timeDiff < oneHour && userAttempts.count >= 3) {
          throw new Error('Too many password reset attempts. Please wait 1 hour before trying again.');
        }
        
        // Reset count if more than an hour has passed
        if (timeDiff >= oneHour) {
          setResetAttempts(prev => ({
            ...prev,
            [email]: { count: 0, lastAttempt: now }
          }));
        }
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;
      
      // Update rate limiting
      setResetAttempts(prev => ({
        ...prev,
        [email]: { 
          count: (prev[email]?.count || 0) + 1, 
          lastAttempt: now 
        }
      }));
      
      setCurrentEmail(email);
      setCurrentView('reset-sent');
    } catch (error) {
      console.error('❌ Password reset error:', error);
      alert(error instanceof Error ? error.message : 'Failed to send password reset email. Please try again.');
    }
  };

  if (loading) {
    console.log('⏳ Showing loading spinner...');
    return <LoadingSpinner />;
  }

  console.log('🎨 Rendering login form...');
    return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
                <Logo size="lg" variant="full" />
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card py-8 px-6 sm:px-10">
            {currentView === 'login' && (
            <>
              {/* Welcome text only for login view */}
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                  Welcome
                </h2>
                <p className="mt-3 text-sm text-gray-600 leading-5">
                  Sign in to your kridah labs account
                </p>
              </div>
              <LoginForm onLogin={handleLogin} onForgotPassword={handleForgotPassword} />
            </>
          )}

            {currentView === 'forgot-password' && (
              <ForgotPasswordView
              onResetPassword={handleResetPassword}
                onBackToLogin={handleBackToLogin}
              />
            )}

            {currentView === 'reset-sent' && (
              <ResetSentView
                onBackToLogin={handleBackToLogin}
                email={currentEmail}
              />
            )}
          </div>
        </div>
    </div>
  );
};

// Forgot Password Component
const ForgotPasswordView: React.FC<{
  onResetPassword: (email: string) => Promise<void>;
  onBackToLogin: () => void;
}> = ({ onResetPassword, onBackToLogin }) => {
  const [email, setEmail] = useState('user4@kridah.com');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    try {
      await onResetPassword(email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Reset your password</h3>
        <p className="mt-2 text-sm text-gray-600 leading-5">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="reset-email" className="form-label">
            Email Address
          </label>
          <input
            id="reset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-field"
            placeholder="Enter your email"
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary"
        >
          {isLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Sending...</span>
            </div>
          ) : (
            'Send Reset Link'
          )}
        </button>

        <div className="text-center">
          <button
            type="button"
            onClick={onBackToLogin}
            className="text-sm text-blue-600 hover:text-blue-500 font-medium"
          >
            Back to Sign In
          </button>
        </div>
      </form>
    </div>
  );
};

// Reset Sent Component
const ResetSentView: React.FC<{
  onBackToLogin: () => void;
  email: string;
}> = ({ onBackToLogin, email }) => {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg
            className="h-6 w-6 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Check your email</h3>
        <p className="mt-2 text-sm text-gray-600 leading-5">
          We've sent a password reset link to <strong>{email}</strong>
        </p>
        <p className="mt-2 text-sm text-gray-600 leading-5">
          Please check your inbox and click the link to reset your password.
        </p>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={onBackToLogin}
          className="text-sm text-blue-600 hover:text-blue-500 font-medium"
        >
          Back to Sign In
        </button>
      </div>
    </div>
  );
};

export default LoginPage;
